import { clsx } from 'clsx';
import { ChevronDown, XIcon } from 'lucide-react';
import ReactSelect, {
  type ClearIndicatorProps,
  components,
  type DropdownIndicatorProps,
  type GroupBase,
  type MultiValueRemoveProps,
  type Props,
  type StylesConfig,
} from 'react-select';

/**
 * This component is a wrapper around the react-select library.
 * It is used to create a multi-select dropdown with custom styles.
 * It has been inspired by the following article:
 * https://www.jussivirtanen.fi/writing/styling-react-select-with-tailwind
 */

function DropdownIndicator(props: DropdownIndicatorProps<Option>) {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDown className="h-4 w-4" />
    </components.DropdownIndicator>
  );
}

function ClearIndicator(props: ClearIndicatorProps<Option>) {
  return (
    <components.ClearIndicator {...props}>
      <XIcon className="h-4 w-4" />
    </components.ClearIndicator>
  );
}

function MultiValueRemove(props: MultiValueRemoveProps) {
  return (
    <components.MultiValueRemove {...props}>
      <XIcon className="h-4 w-4" />
    </components.MultiValueRemove>
  );
}

const controlStyles = {
  base: 'flex w-full min-h-[40px] p-1 rounded-md border border-input bg-background px-3 text-sm shadow-sm ring-offset-background',
  focus: 'ring-2 ring-ring ring-offset-2',
  nonFocus: 'hover:cursor-pointer',
};

const placeholderStyles = 'text-muted-foreground text-sm';
// text-base on mobile: iOS Safari/Samsung Internet zoom the viewport on focus of inputs < 16px
const selectInputStyles = 'text-base md:text-sm';
const valueContainerStyles = 'gap-1';
const singleValueStyles = 'text-sm';
const multiValueStyles =
  'bg-primary text-primary-foreground rounded-md items-center py-1 pl-2 pr-1 gap-1';
const multiValueLabelStyles = 'text-sm';
const multiValueRemoveStyles = 'rounded-md hover:bg-destructive hover:text-destructive-foreground';
const indicatorsContainerStyles = 'gap-1';
const clearIndicatorStyles = 'text-muted-foreground hover:text-destructive p-1 rounded-md';
const indicatorSeparatorStyles = ''; // Do not show separator
const dropdownIndicatorStyles = 'text-muted-foreground hover:text-foreground p-1 rounded-md';

const menuStyles = 'mt-1 rounded-md border bg-popover text-popover-foreground shadow-md p-1';
const menuListStyles = 'rounded-md bg-popover text-popover-foreground';
const groupHeadingStyles = 'px-2 py-1.5 text-sm font-semibold text-muted-foreground';
const optionStyles = {
  base: 'relative flex w-full cursor-default rounded-md select-none items-center px-3 py-1.5 text-sm outline-none',
  focus: 'bg-accent text-accent-foreground',
  selected: 'bg-primary/50 text-primary-foreground rounded-none',
};
const noOptionsMessageStyles = 'p-2 text-sm text-muted-foreground';

const defaultStyles: StylesConfig<Option, true, GroupBase<Option>> = {
  // On mobile, the label will truncate automatically, so we want to
  // override that behavior.
  multiValueLabel: base => ({
    ...base,
    whiteSpace: 'normal',
    overflow: 'visible',
  }),
  control: base => ({
    ...base,
    transition: 'none',
    minHeight: '40px',
  }),
  menu: base => ({
    ...base,
    fontSize: '0.875rem', // equivalent to text-sm
  }),
};

type Option = {
  label: string;
  value: string;
};

interface MultiSelectProps<Option> extends Props<Option, true, GroupBase<Option>> {
  portalTarget?: HTMLElement | null;
}

function MultiSelect(props: MultiSelectProps<Option>) {
  const { portalTarget = document.body, ...restProps } = props;

  return (
    <ReactSelect
      isMulti
      placeholder="Sélectionner..."
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      unstyled
      styles={{
        ...defaultStyles,
        ...restProps.styles,
      }}
      components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
      classNames={{
        control: ({ isFocused }) =>
          clsx(isFocused ? controlStyles.focus : controlStyles.nonFocus, controlStyles.base),
        placeholder: () => placeholderStyles,
        input: () => selectInputStyles,
        valueContainer: () => valueContainerStyles,
        singleValue: () => singleValueStyles,
        multiValue: () => multiValueStyles,
        multiValueLabel: () => multiValueLabelStyles,
        multiValueRemove: () => multiValueRemoveStyles,
        indicatorsContainer: () => indicatorsContainerStyles,
        clearIndicator: () => clearIndicatorStyles,
        indicatorSeparator: () => indicatorSeparatorStyles,
        dropdownIndicator: () => dropdownIndicatorStyles,
        menu: () => menuStyles,
        menuList: () => menuListStyles,
        groupHeading: () => groupHeadingStyles,
        option: ({ isFocused, isSelected }) =>
          clsx(
            isFocused && optionStyles.focus,
            isSelected && optionStyles.selected,
            optionStyles.base,
          ),
        noOptionsMessage: () => noOptionsMessageStyles,
      }}
      menuPortalTarget={portalTarget}
      {...restProps}
    />
  );
}

export { MultiSelect };
